/** Microsoft WAVE_FORMAT_MPEG (Layer I/II in a WAV container). */
const WAVE_FORMAT_MPEG = 80;
/** Microsoft WAVE_FORMAT_MPEGLAYER3 (MP3 in a WAV container). */
const WAVE_FORMAT_MPEGLAYER3 = 85;

export type MpegWavLayer = 1 | 2 | 3;

export interface UnwrappedMpegWav {
  blob: Blob;
  payload: Uint8Array;
  audioFormat: number;
  layer: MpegWavLayer | null;
}

/**
 * Reads a four-character RIFF chunk id from `bytes` at `start`.
 */
function ascii(bytes: Uint8Array, start: number, len: number) {
  return String.fromCharCode(...bytes.subarray(start, start + len));
}

/**
 * Reads the MPEG layer from a frame sync header (Layer III = MP3).
 */
function layerFromMpegFrame(bytes: Uint8Array, offset: number): MpegWavLayer | null {
  if (offset + 2 > bytes.byteLength) {
    return null;
  }
  if (bytes[offset] !== 0xff || (bytes[offset + 1] & 0xe0) !== 0xe0) {
    return null;
  }
  const layerBits = (bytes[offset + 1] >> 1) & 0x3;
  if (layerBits === 1) {
    return 3;
  }
  if (layerBits === 2) {
    return 2;
  }
  if (layerBits === 3) {
    return 1;
  }
  return null;
}

/**
 * If the buffer is a WAV/BWF file that stores MPEG audio (MP2/MP3) instead of
 * PCM, returns the elementary stream so the browser can play it as audio/mpeg.
 * Native HTML audio cannot decode WAVE_FORMAT_MPEG (0x50) containers.
 */
export function unwrapMpegWav(buffer: ArrayBuffer): UnwrappedMpegWav | null {
  const bytes = new Uint8Array(buffer);
  if (bytes.byteLength < 12 || ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WAVE') {
    return null;
  }

  const view = new DataView(buffer);
  let cursor = 12;
  let audioFormat: number | null = null;
  let dataOffset: number | null = null;
  let dataSize: number | null = null;

  while (cursor + 8 <= bytes.byteLength) {
    const id = ascii(bytes, cursor, 4);
    const size = view.getUint32(cursor + 4, true);
    if (size < 0) {
      break;
    }
    if (id === 'fmt ' && cursor + 10 <= bytes.byteLength) {
      audioFormat = view.getUint16(cursor + 8, true);
    }
    if (id === 'data') {
      dataOffset = cursor + 8;
      dataSize = size;
      break;
    }
    cursor += 8 + size + (size % 2);
  }

  if (
    audioFormat == null ||
    (audioFormat !== WAVE_FORMAT_MPEG && audioFormat !== WAVE_FORMAT_MPEGLAYER3) ||
    dataOffset == null ||
    dataSize == null
  ) {
    return null;
  }

  const end = Math.min(dataOffset + dataSize, bytes.byteLength);
  const payload = bytes.subarray(dataOffset, end);

  return {
    blob: new Blob([payload], { type: 'audio/mpeg' }),
    payload,
    audioFormat,
    layer: layerFromMpegFrame(payload, 0),
  };
}
